/**
 * Types for Lev Learn: System Card, the monthly read of every new AI model
 * release (parent LEV-2909).
 *
 * Rich text fields end in `Html` and hold inline HTML (links, strong, em and
 * a handful of other inline tags, never block markup). They render through
 * `TrustedInlineHtml` (`src/components/learn/systemCard/TrustedInlineHtml.tsx`)
 * the same way guide bodies render trusted HTML, gated by the allowlist in
 * `src/lib/learn/trustedHtmlAllowlist.ts`.
 */

export type RichHtml = string;

export interface TableCell {
  html: RichHtml;
  /** Numeric or string sort key for PriceMap-style sortable tables; falls back to the cell text when absent. */
  sortValue?: number | string;
}

export interface DataTable {
  columns: string[];
  /** Every row MUST have exactly `columns.length` cells; enforced by learn.systemCards.test.ts. */
  rows: TableCell[][];
}

export interface ChangeCard {
  /** Labs or companies the card is about, shown before the kicker. MUST be non-empty. */
  vendors: string[];
  kicker: string;
  title: string;
  bodyHtml: RichHtml;
}

export interface Lesson {
  title: string;
  bodyHtml: RichHtml;
}

export interface Commentary {
  title: string;
  paragraphsHtml: RichHtml[];
  calloutHtml: RichHtml;
}

export interface SystemCardEdition {
  /** The month the edition reviews, e.g. "2026-10" (published November 1); also the URL segment. MUST match /^\d{4}-\d{2}$/ and be unique across editions. */
  slug: string;
  /** "System Card, October 2026": named for the month reviewed, not the publish month. */
  title: string;
  eyebrow: string;
  lede: string;
  /** ISO date; render as month day, year. */
  publishedOn: string;
  coversFrom: string;
  coversTo: string;
  pricesCheckedOn: string;
  changes: ChangeCard[];
  cardsRead: { introHtml: RichHtml; table: DataTable; platformNotesHtml: RichHtml };
  releaseMap: { introHtml: RichHtml; velocityHtml: RichHtml; updatedOn: string };
  jobs: { introHtml: RichHtml; table: DataTable };
  /** Picks for chat and voice, where response time is part of the product. */
  speed: { introHtml: RichHtml; table: DataTable; notesHtml: RichHtml };
  /** Sortable; cells carry sortValue. */
  priceMap: { introHtml: RichHtml; table: DataTable };
  watch: RichHtml[];
  developers: { introHtml: RichHtml; table: DataTable; sourcesHtml: RichHtml };
  moving: { introHtml: RichHtml; commentaries: Commentary[]; sourcesHtml: RichHtml };
  lev: {
    introHtml: RichHtml;
    lessons: Lesson[];
    bench: { title: string; introHtml: RichHtml; table: DataTable };
    howWeRunThis: { title: string; paragraphsHtml: RichHtml[]; steps: RichHtml[] };
  };
  goDeeper: { introHtml: RichHtml; table: DataTable };
  method: { bodyHtml: RichHtml; sourcesHtml: RichHtml };
  /** The edition's ready-to-post LinkedIn text (plain, short), copied by the header's "Copy a LinkedIn post". */
  linkedInPost: string;
  /** {month} and {url} are substituted at render time. */
  shareCredit: string;
}

/**
 * Runtime list of release-map lanes, one per lab. `ReleaseLane` is derived
 * from this tuple rather than declared separately, so a lane added to the
 * type automatically becomes a value callers (and the membership test) can
 * check against, and the two can never drift apart.
 */
export const RELEASE_LANES = [
  "OpenAI",
  "Anthropic",
  "Google",
  "Google (Gemma)",
  "xAI",
  "Meta",
  "Mistral",
  "DeepSeek",
  "Alibaba",
  "Moonshot (Kimi)",
  "Zhipu (GLM)",
  "MiniMax",
  "NVIDIA",
  "Xiaomi",
  "OpenAI (gpt-oss)",
  "Other open",
] as const;

export type ReleaseLane = (typeof RELEASE_LANES)[number];

export type ReleaseModality = "text" | "agent" | "voice" | "image" | "video" | "science" | string;

export interface ModelRelease {
  /** ISO date. */
  d: string;
  /** One row per lab on the map; closed labs above the line, open below. */
  lane: ReleaseLane;
  /** Model name. */
  m: string;
  /** Open weights. */
  open: boolean;
  /** Announced, not yet released; drawn hollow and excluded from counts. */
  pending: boolean;
  mod: ReleaseModality;
  /** Generational release; larger labeled dot. */
  major: boolean;
  /** Two or three facts shown in the detail panel. */
  f: string[];
  /** The card or release post. */
  src: string;
}
